import { Trans, useTranslation } from 'next-i18next'
import styled from 'styled-components'
import { head, pickAll } from 'ramda'
import { createAlert } from '@/api/listing-alerts'
import {
  Box,
  Dialog,
  Stack,
  Text,
  TextLink,
  useToast
} from '@machobear/design-system'
import { useMutation } from '@tanstack/react-query'
import { Form, Formik } from 'formik'

import Yup from '@/lib/yup'
import { useFilters } from '@/hooks/use-filters'
import { show as showLoginModal } from '@/features/Auth/login-modal'
import { Button } from '@/components/button-new'
import { FieldInput } from '@/components/form'

const FormWrapper = styled(Form)`
  width: 100%;
`

interface Props {
  isLoggedIn: boolean
  open: boolean
  onClose: () => void
}

type Form = {
  name: string
  email?: string
}

const handleAlertRedirect = () => {
  if (typeof window === 'undefined') return
  window.location.href = `/saved-alerts`
}

export const AlertConfirmationModal: React.FC<Props> = ({
  isLoggedIn = false,
  open,
  onClose
}) => {
  const [filters] = useFilters()
  const filterValues = pickAll(
    [
      'cities',
      'housing_types',
      'unit_types',
      'bedroom_count',
      'bathroom_count',
      'pet_policy',
      'min_price',
      'max_price',
      'availability_date',
      'custom_availability_date',
      'search_only_verified',
      'min_size',
      'lease_type',
      'rental_type',
      'has_3d_tour',
      'features',
      'utilities'
    ],
    filters
  )
  const { t } = useTranslation()
  const showToast = useToast()

  const { mutateAsync: createAlertMethod } = useMutation({
    mutationFn: createAlert,
    onSuccess: async () => {
      showToast({
        message: t(
          'b.listing.alert.success_created.message',
          'Your alert has been created!'
        ),
        tone: 'positive',
        action: {
          label: t('b.listing.alert.manage_link.message', 'Manage alert'),
          onClick: () => {
            window.open('/saved-alerts', '_blank')
          }
        }
      })
    },
    onError: async () => {
      showToast({
        message: t(
          'b.listing.alert.error.message',
          'Oops! Something went wrong while creating your listing alert.'
        ),
        tone: 'critical'
      })
    }
  })

  const handleAlert = async ({ name, email }: Form) => {
    if (isLoggedIn) {
      await createAlertMethod({
        name,
        criteria: filterValues,
        isLoggedIn
      })

      onClose()
    } else {
      onClose()
      showLoginModal({
        mode: 'registerAlert',
        eventId: 'web-signup-submit-alertbutton',
        data: { username: email },
        afterLogin: async () => {
          await createAlertMethod({ name, criteria: filterValues })
          handleAlertRedirect()
        },
        t
      })
    }
  }

  const formOptions = () => ({
    initialValues: {
      name: `${head(filters.cities)} ${t(
        'b.listings.view.rentals.text',
        'Rentals'
      )}`
    },
    validationSchema: Yup.object().shape({
      name: Yup.string()
        .max(50, t('b.listing.alert.max_char.text', 'Max: 50 characters'))
        .required(),
      ...(!isLoggedIn
        ? {
            email: Yup.string().email().required()
          }
        : {})
    }),
    onSubmit: handleAlert,
    validateOnMount: true
  })

  return (
    <Dialog
      id="alert-confirmation-dialog"
      title={t('b.listing.alert.create.title', 'Create Alert')}
      open={open}
      onClose={onClose}
      width="xsmall"
    >
      <Stack space="gutter">
        <Text size="small">
          {t(
            'b.listing.alert.create.text',
            'We will alert you when new listings match your search criteria.'
          )}
        </Text>

        <Formik {...formOptions()}>
          {({ isSubmitting }) => (
            <>
              <FormWrapper>
                <Stack space="gutter">
                  <FieldInput
                    id="name"
                    name="name"
                    placeholder={t(
                      'b.listing.alert.alert_name.name.input',
                      'Alert Name'
                    )}
                    required
                  />
                  {!isLoggedIn && (
                    <FieldInput
                      id="email"
                      name="email"
                      placeholder={t('common.email', 'Email')}
                      required
                    />
                  )}
                  <Trans
                    t={t}
                    i18nKey="b.listing.alert.create_disclaimer_terms.text"
                  >
                    <Text size="xsmall">
                      By clicking &quot;Create Alert&quot;, I confirm that I
                      have read and agree to liv.rent&apos;s{' '}
                      <TextLink href="/terms" target="_blank">
                        Terms & Conditions
                      </TextLink>{' '}
                      and{' '}
                      <TextLink href="/privacy" target="_blank">
                        Privacy Policy
                      </TextLink>
                    </Text>
                  </Trans>
                </Stack>

                <Box paddingTop="large">
                  <Button
                    width="100%"
                    id={`create-alert-modal-click`}
                    theme="primary"
                    type="submit"
                    disabled={isSubmitting}
                  >
                    {t('b.listing.alert.create.button', 'Create Alert')}
                  </Button>
                </Box>
              </FormWrapper>
            </>
          )}
        </Formik>
      </Stack>
    </Dialog>
  )
}
